import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Globe, Copy, Check, ShieldCheck, ShieldAlert, Loader2, ArrowRight, Trash2, RefreshCw, ExternalLink, Info, Lock, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  getMyBrand, setCustomDomain, removeCustomDomain, verifyCustomDomain, getDomainInstructions,
} from '../lib/api';

export default function AccountDomain() {
  const nav = useNavigate();
  const { user, brand, setBrand, loading: authLoading } = useAuth();
  const [b, setB] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav('/login'); return; }
    (async () => {
      try {
        const brandData = await getMyBrand();
        setB(brandData);
        setDomainInput(brandData.custom_domain || '');
        if (brandData.custom_domain) {
          const inst = await getDomainInstructions();
          setInstructions(inst);
        }
      } catch { toast.error('Errore caricamento'); }
    })();
  }, [user, authLoading, nav]);

  if (!b) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0d63b0]" /></div>;

  const isPro = b.plan === 'pro';

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success('Copiato');
      setTimeout(() => setCopied(''), 1600);
    } catch { toast.error('Impossibile copiare'); }
  };

  const save = async () => {
    if (!domainInput.trim()) return toast.error('Inserisci un dominio');
    setSaving(true);
    try {
      const res = await setCustomDomain(domainInput.trim());
      setB(res.brand);
      setBrand(res.brand);
      setInstructions(res.dns_instructions);
      setVerifyResult(null);
      toast.success('Dominio salvato', { description: 'Configura ora i record DNS.' });
    } catch (e) {
      const d = e?.response?.data?.detail || '';
      if (d === 'upgrade_required_pro') toast.error('Serve il piano Pro', { description: 'Passa al piano Pro per abilitare il dominio custom.' });
      else if (d === 'invalid_domain') toast.error('Dominio non valido');
      else if (d === 'domain_taken') toast.error('Dominio già registrato da un altro brand');
      else toast.error('Errore', { description: d });
    } finally { setSaving(false); }
  };

  const verify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await verifyCustomDomain();
      setVerifyResult(res);
      setB(res.brand);
      setBrand(res.brand);
      if (res.verified) toast.success('Dominio verificato', { description: 'Il tuo dominio è attivo!' });
      else toast.error('Verifica fallita', { description: res.brand?.domain_check_error || 'Controlla i record DNS.' });
    } catch (e) {
      toast.error('Errore verifica');
    } finally { setVerifying(false); }
  };

  const remove = async () => {
    if (!confirm('Rimuovere il dominio custom? Il brand tornerà a usare l\'URL /b/slug.')) return;
    try {
      const nb = await removeCustomDomain();
      setB(nb);
      setBrand(nb);
      setInstructions(null);
      setDomainInput('');
      setVerifyResult(null);
      toast.success('Dominio rimosso');
    } catch { toast.error('Errore'); }
  };

  return (
    <div className="py-10">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#0d63b0]">Pannello Reseller · DNS</div>
            <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-3"><Globe className="h-8 w-8 text-[#0d63b0]" /> Dominio personalizzato</h1>
            <p className="mt-1 text-slate-600 text-sm">Fai puntare un tuo dominio ai server Tecnologie e servi il tuo brand con il tuo indirizzo.</p>
          </div>
          <Button asChild variant="outline"><Link to="/account/brand"><ArrowRight className="h-4 w-4 mr-1.5 rotate-180" /> Torna al brand</Link></Button>
        </div>

        {/* Plan gate */}
        {!isPro && (
          <Card className="border border-amber-200 bg-amber-50/70 p-5 mb-6">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-amber-700 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-amber-900">Funzionalità piano Pro</div>
                <div className="text-sm text-amber-800 mt-1">Il dominio custom è disponibile solo con il piano Pro. Puoi comunque simulare l'upgrade in modalità DEMO.</div>
              </div>
              <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white"><Link to="/account/subscription">Passa a Pro</Link></Button>
            </div>
          </Card>
        )}

        {/* Domain input */}
        <Card className={`border border-slate-200 p-6 mb-6 ${!isPro ? 'opacity-90' : ''}`}>
          <div className="font-semibold text-slate-900 flex items-center gap-2"><Globe className="h-4 w-4 text-[#0d63b0]" /> Il tuo dominio</div>
          <p className="text-xs text-slate-500 mt-1">Esempio: <code className="font-mono">secure.acme.it</code>, <code className="font-mono">privato.tuobrand.com</code></p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Input value={domainInput} onChange={(e) => setDomainInput(e.target.value)} placeholder="secure.tuobrand.it" className="flex-1 font-mono" disabled={!isPro} />
            <Button onClick={save} disabled={!isPro || saving} className="bg-[#0d63b0] hover:bg-[#0a4f8f] text-white shrink-0">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> {b.custom_domain ? 'Aggiorna' : 'Salva dominio'}</>)}
            </Button>
            {b.custom_domain && (
              <Button onClick={remove} variant="outline" className="text-red-600 border-red-200 hover:border-red-300 hover:text-red-700"><Trash2 className="h-4 w-4 mr-1.5" /> Rimuovi</Button>
            )}
          </div>
          {b.custom_domain && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-slate-600">Stato:</span>
              {b.domain_verified ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-semibold"><ShieldCheck className="h-3.5 w-3.5" /> Verificato</span>
              ) : b.domain_status === 'failed' ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 px-3 py-1 text-xs font-semibold"><ShieldAlert className="h-3.5 w-3.5" /> Verifica fallita</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1 text-xs font-semibold"><Loader2 className="h-3.5 w-3.5" /> In attesa verifica</span>
              )}
              {b.domain_verified_at && <span className="text-xs text-slate-500">Verificato il {new Date(b.domain_verified_at).toLocaleString('it-IT')}</span>}
              {b.domain_check_error && !b.domain_verified && <span className="text-xs text-red-600">· {b.domain_check_error}</span>}
            </div>
          )}
        </Card>

        {/* DNS Records */}
        {instructions && instructions.records && (
          <Card className="border border-slate-200 p-6 mb-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-slate-900 flex items-center gap-2"><Globe className="h-4 w-4 text-[#0d63b0]" /> Configurazione DNS</div>
                <p className="text-sm text-slate-600 mt-1">Accedi al pannello del tuo registrar (es. Aruba, Register.it, Cloudflare) e aggiungi questi due record. La propagazione DNS può richiedere fino a 24 ore.</p>
              </div>
              <Button onClick={verify} disabled={verifying} className="bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
                {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><RefreshCw className="h-4 w-4 mr-1.5" /> Verifica DNS ora</>)}
              </Button>
            </div>

            <div className="mt-5 space-y-4">
              {instructions.records.map((rec, i) => {
                const isTxt = rec.type === 'TXT';
                const key = `${rec.type}-${i}`;
                const okThis = verifyResult && ((isTxt && verifyResult.txt_ok) || (!isTxt && verifyResult.cname_ok));
                const failThis = verifyResult && !okThis;
                return (
                  <div key={key} className={`rounded-lg border p-4 ${okThis ? 'border-emerald-200 bg-emerald-50/50' : failThis ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-slate-50/50'}`}>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center rounded-md text-[11px] font-bold px-2 py-0.5 border ${rec.type === 'CNAME' ? 'bg-blue-50 text-[#0d63b0] border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                        {rec.type}
                      </span>
                      <span className="text-xs text-slate-500">{rec.purpose}</span>
                      <div className="flex-1" />
                      {okThis && <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><ShieldCheck className="h-3.5 w-3.5" /> OK</span>}
                      {failThis && <span className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold"><ShieldAlert className="h-3.5 w-3.5" /> Non trovato</span>}
                    </div>
                    <div className="mt-3 grid md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Host / Nome</div>
                        <div className="mt-1 flex items-center gap-2">
                          <code className="font-mono bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 flex-1 truncate">{rec.host}</code>
                          <button onClick={() => copy(rec.host, `${key}-host`)} className="p-1.5 text-slate-500 hover:text-[#0d63b0]" title="Copia">
                            {copied === `${key}-host` ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Valore / Destinazione</div>
                        <div className="mt-1 flex items-center gap-2">
                          <code className="font-mono bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 flex-1 truncate">{rec.value}</code>
                          <button onClick={() => copy(rec.value, `${key}-value`)} className="p-1.5 text-slate-500 hover:text-[#0d63b0]" title="Copia">
                            {copied === `${key}-value` ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-[11px] text-slate-500">TTL consigliato: {rec.ttl} secondi (1 ora)</div>
                  </div>
                );
              })}
            </div>

            {verifyResult && !verifyResult.verified && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4 text-sm">
                <div className="font-semibold text-amber-900 flex items-center gap-2"><Info className="h-4 w-4" /> Debug della verifica</div>
                <div className="mt-2 text-amber-800 space-y-1 text-xs font-mono">
                  <div>CNAME trovati: {verifyResult.cname_found?.length ? verifyResult.cname_found.join(', ') : '(nessuno)'}</div>
                  <div>TXT trovati: {verifyResult.txt_found?.length ? verifyResult.txt_found.join(', ') : '(nessuno)'}</div>
                </div>
                <div className="mt-2 text-xs text-amber-800">Se hai appena configurato i record, attendi qualche minuto e riprova. La propagazione può richiedere fino a 24 ore.</div>
              </div>
            )}

            {b.domain_verified && (
              <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
                <div className="flex items-center gap-2 text-emerald-800">
                  <ShieldCheck className="h-5 w-5" />
                  <div>
                    <div className="font-semibold">Il tuo dominio è attivo!</div>
                    <div className="text-sm">I tuoi link saranno accessibili tramite <a href={`https://${b.custom_domain}`} target="_blank" rel="noreferrer" className="font-mono font-semibold underline">{b.custom_domain}</a> <ExternalLink className="inline h-3 w-3" /></div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Guide */}
        <Card className="border border-slate-200 p-6">
          <div className="font-semibold text-slate-900 flex items-center gap-2"><Info className="h-4 w-4 text-[#0d63b0]" /> Guida rapida</div>
          <ol className="mt-4 space-y-3 text-sm text-slate-700">
            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-blue-50 text-[#0d63b0] font-bold flex items-center justify-center shrink-0">1</span> Inserisci il tuo dominio (es. <code className="font-mono">secure.tuobrand.it</code>) nel campo sopra e clicca <strong>Salva</strong>.</li>
            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-blue-50 text-[#0d63b0] font-bold flex items-center justify-center shrink-0">2</span> Accedi al pannello DNS del tuo registrar e aggiungi i due record indicati (CNAME e TXT).</li>
            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-blue-50 text-[#0d63b0] font-bold flex items-center justify-center shrink-0">3</span> Attendi qualche minuto (la propagazione può richiedere fino a 24h) e clicca <strong>Verifica DNS ora</strong>.</li>
            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-blue-50 text-[#0d63b0] font-bold flex items-center justify-center shrink-0">4</span> Una volta verificato, i push del tuo brand saranno disponibili su <strong>https://tuodominio.it</strong>.</li>
          </ol>
          <div className="mt-5 text-xs text-slate-500">Provider supportati: Aruba, Register.it, Cloudflare, GoDaddy, OVH, Namecheap e qualsiasi provider DNS standard.</div>
        </Card>
      </div>
    </div>
  );
}
